import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticMotor } from "./realistic";

/**
 * MotorNode – SVG & Realistic motor visualization.
 *
 * Supports:
 * - Realistic 3D SCADA design with rotating cooling fan & telemetry
 * - Glassmorphism design layout
 * - Classic industrial SVG layout
 */
const MotorNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticMotor
              liveValue={liveValue}
              unit={unit || "RPM"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const strokeColor = isRunning ? color : "#94a3b8";
        const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";
        const rotateDuration = isRunning ? `${1 / (speedScale || 1)}s` : "0s";
        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="80"
            height="72"
            viewBox="0 0 80 72"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  <linearGradient id="motorGlassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="motorActiveGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={color} stopOpacity="1" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.5" />
                  </linearGradient>
                  <filter id="motorGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Active electromagnetic/vibration wave around motor */}
                {isRunning && (
                  <rect x="8" y="8" width="54" height="44" rx="7" stroke={color} strokeWidth="1.5" fill="none" opacity="0.3" filter="url(#motorGlow)">
                    <animate attributeName="stroke-width" values="1;3;1" dur="1.2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.1;0.6;0.1" dur="1.2s" repeatCount="indefinite" />
                  </rect>
                )}

                {/* Motor housing */}
                <rect
                  x="10"
                  y="10"
                  width="50"
                  height="40"
                  rx="6"
                  fill="url(#motorGlassGrad)"
                  stroke={isRunning ? "url(#motorActiveGrad)" : "#475569"}
                  strokeWidth="2.5"
                  style={{
                    filter: isRunning ? "url(#motorGlow)" : "none",
                    transition: "all 0.3s ease",
                  }}
                />

                {/* Motor "M" label */}
                <text x="35" y="36" textAnchor="middle" fontSize="16" fontWeight="900" fill={color} style={{ letterSpacing: 1 }}>
                  M
                </text>

                {/* Shaft circle (right end) — rotates when running */}
                <g
                  style={{
                    transformOrigin: "70px 30px",
                    animation: isRunning ? `live-motor-rotate ${rotateDuration} linear infinite` : "none",
                  }}
                  filter={isRunning ? "url(#motorGlow)" : "none"}
                >
                  <circle cx="70" cy="30" r="13" fill="url(#motorGlassGrad)" stroke={strokeColor} strokeWidth="2" />
                  {/* Thicker multi-bladed propeller style spokes */}
                  <path d="M 70,30 L 70,18 M 70,30 L 70,42 M 70,30 L 58,30 M 70,30 L 82,30" stroke={strokeColor} strokeWidth="2.5" strokeLinecap="round" />
                  <path d="M 70,30 L 61,21 M 70,30 L 79,39 M 70,30 L 61,39 M 70,30 L 79,21" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" />
                </g>

                {/* Shaft line */}
                <line x1="60" y1="30" x2="80" y2="30" stroke={strokeColor} strokeWidth="2.5" />

                {/* Cooling fins */}
                <line x1="15" y1="15" x2="15" y2="45" stroke={color} strokeWidth="1.5" opacity="0.5" />
                <line x1="20" y1="15" x2="20" y2="45" stroke={color} strokeWidth="1.5" opacity="0.5" />
                <line x1="25" y1="15" x2="25" y2="45" stroke={color} strokeWidth="1.5" opacity="0.5" />

                {/* Premium status dot - top right */}
                <circle cx="60" cy="10" r="6" fill={dotColor} stroke="#ffffff" strokeWidth="2" filter={isRunning ? "url(#motorGlow)" : "none"} />
                <circle
                  cx="60"
                  cy="10"
                  r="6"
                  fill="none"
                  stroke={dotColor}
                  strokeWidth="2.5"
                  opacity={isRunning ? "0.6" : "0"}
                  style={{
                    animation: isRunning ? "live-motor-pulse 2s ease-in-out infinite" : "none",
                  }}
                />

                {/* Status text - clearly below motor housing */}
                <text
                  x="35"
                  y="64"
                  textAnchor="middle"
                  fontSize="7"
                  fontWeight="800"
                  fill={dotColor}
                  style={{ letterSpacing: 0.5 }}
                >
                  {statusColor === "green" ? "RUNNING" : statusColor === "red" ? "STOPPED" : "OFFLINE"}
                </text>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Motor housing */}
                <rect
                  x="10"
                  y="10"
                  width="50"
                  height="40"
                  rx="4"
                  fill={isRunning ? `${color}15` : `${color}08`}
                  stroke={strokeColor}
                  strokeWidth="2"
                />

                {/* Motor "M" label */}
                <text x="35" y="36" textAnchor="middle" fontSize="16" fontWeight="bold" fill={color}>
                  M
                </text>

                {/* Shaft circle (right end) — rotates when running */}
                <g
                  style={{
                    transformOrigin: "70px 30px",
                    animation: isRunning ? `live-motor-rotate ${rotateDuration} linear infinite` : "none",
                  }}
                >
                  <circle cx="70" cy="30" r="10" fill={`${color}15`} stroke={color} strokeWidth="2" />
                  {/* Shaft cross indicator */}
                  <line x1="63" y1="30" x2="77" y2="30" stroke={color} strokeWidth="1.5" opacity="0.6" />
                  <line x1="70" y1="23" x2="70" y2="37" stroke={color} strokeWidth="1.5" opacity="0.6" />
                </g>

                {/* Shaft line */}
                <line x1="60" y1="30" x2="80" y2="30" stroke={color} strokeWidth="2" />

                {/* Cooling fins */}
                <line x1="12" y1="15" x2="12" y2="45" stroke={color} strokeWidth="1" opacity="0.4" />
                <line x1="16" y1="15" x2="16" y2="45" stroke={color} strokeWidth="1" opacity="0.4" />
                <line x1="20" y1="15" x2="20" y2="45" stroke={color} strokeWidth="1" opacity="0.4" />
                <line x1="24" y1="15" x2="24" y2="45" stroke={color} strokeWidth="1" opacity="0.4" />

                {/* Status indicator with glow - top right */}
                <circle cx="60" cy="10" r="5" fill={dotColor} />
                <circle
                  cx="60"
                  cy="10"
                  r="5"
                  fill="none"
                  stroke={dotColor}
                  strokeWidth="2"
                  opacity={isRunning ? "0.4" : "0"}
                  style={{
                    animation: isRunning ? "live-motor-pulse 2s ease-in-out infinite" : "none",
                  }}
                />

                {/* Terminal box (top) */}
                <rect x="28" y="2" width="14" height="8" rx="2" fill={`${color}20`} stroke={color} strokeWidth="1.5" />

                {/* Power leads */}
                <line x1="32" y1="0" x2="32" y2="2" stroke={color} strokeWidth="1.5" />
                <line x1="35" y1="0" x2="35" y2="2" stroke={color} strokeWidth="1.5" />
                <line x1="38" y1="0" x2="38" y2="2" stroke={color} strokeWidth="1.5" />

                {/* Input connector (left) */}
                <rect x="0" y="26" width="10" height="8" fill={color} rx="2" />

                {/* Status text */}
                <text
                  x="35"
                  y="56"
                  textAnchor="middle"
                  fontSize="7"
                  fontWeight="600"
                  fill={dotColor}
                >
                  {statusColor === "green" ? "RUNNING" : statusColor === "red" ? "STOPPED" : "OFFLINE"}
                </text>
              </>
            )}
          </svg>
        );
      }}
    </BaseNode>
  );
};

MotorNode.propTypes = {
  data: PropTypes.object,
  selected: PropTypes.bool,
  id: PropTypes.string,
};

export default MotorNode;
